import React from 'react'
import cx from 'classnames'
import { Link as ReactRouterLink } from 'react-router-dom'
import * as Icon from '@manychat/icons'

import cleanProps from '../../lib/clean-react-props'
import { fixedForwardRef } from '../../utils/fixedForwardRef'
import Loader from '../Loader'

import {
  ButtonAsButtonProps,
  ButtonAsAnchorProps,
  ButtonAsRouterLinkProps,
  As,
  IconNames,
  BtnV2Props,
  IconSizes,
} from './interfaces'

import cm from './BtnV2.module.css'

export type { BtnV2Props }

export const sizeMapping: Record<IconSizes, number> = {
  default: 20,
  small: 16,
  large: 24,
}

function isAnchorProps(props: { as?: As }): props is ButtonAsAnchorProps<'a'> {
  return props.as === 'a'
}

function isRouterLinkProps(props: {
  as?: As
}): props is ButtonAsRouterLinkProps<typeof ReactRouterLink> {
  return props.as === ReactRouterLink
}

const BtnV = <T extends As = 'button'>(
  props: BtnV2Props<T>,
  ref: React.ForwardedRef<HTMLElement>,
) => {
  const {
    label,
    size = 'default',
    variant = 'default',
    disabled,
    fullWidth = false,
    loading = false,
    leftIcon,
    rightIcon,
    onlyIcon,
    children,
    className,
    ariaLabelledBy,
    ariaLabel,
    ariaExpanded,
    tabIndex,
    ...rest
  } = props
  const restProps = rest
  const LeftIconComponent = leftIcon ? Icon[leftIcon as IconNames] : null
  const RightIconComponent = rightIcon ? Icon[rightIcon as IconNames] : null
  const OnlyIconComponent = onlyIcon ? Icon[onlyIcon as IconNames] : null
  const iconToUse = onlyIcon || leftIcon || rightIcon
  const iconSize = sizeMapping[size]

  const isDisabled = disabled || loading
  const linkTabIndex = tabIndex ?? (isDisabled ? -1 : undefined)

  const buttonClasses = cx(
    cm.btnV2,
    {
      [cm.primary]: variant === 'primary',
      [cm.default]: variant === 'default',
      [cm.success]: variant === 'success',
      [cm.danger]: variant === 'danger',
      [cm.dangerLight]: variant === 'danger-light',
      [cm.ghostPrimary]: variant === 'ghost-primary',
      [cm.ghostLight]: variant === 'ghost-light',
      [cm.ghostDark]: variant === 'ghost-dark',
      [cm.defaultSize]: size === 'default',
      [cm.small]: size === 'small',
      [cm.large]: size === 'large' && !iconToUse,
      [cm.largeWithIcon]: size === 'large' && iconToUse,
      [cm.fullWidth]: fullWidth,
      [cm.loading]: loading,
      [cm.onlyIcon]: onlyIcon,
      [cm.leftIcon]: leftIcon,
      [cm.rightIcon]: rightIcon,
      [cm.disabled]: disabled,
    },
    className,
  )

  const content = (
    <>
      {OnlyIconComponent && (
        <OnlyIconComponent aria-hidden="true" focusable="false" size={iconSize} />
      )}
      {loading && (
        <span className={cx('p-stretched', cm.loader)}>
          <Loader center size={iconSize} theme={{ spinner: cx(cm.spinner) }} />
        </span>
      )}
      {!OnlyIconComponent && LeftIconComponent && (
        <LeftIconComponent aria-hidden="true" focusable="false" size={iconSize} />
      )}
      {!OnlyIconComponent && label && <span className={cm.label}>{label}</span>}
      {!OnlyIconComponent && !label && (
        <span className={cx(cm.label, cm.capitalize)}>{children}</span>
      )}
      {!OnlyIconComponent && RightIconComponent && (
        <RightIconComponent aria-hidden="true" focusable="false" size={iconSize} />
      )}
    </>
  )

  if (isAnchorProps(restProps)) {
    const { as: _, ...anchorRestProps } = restProps as ButtonAsAnchorProps<'a'>

    const handleAnchorClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        evt.preventDefault()
      } else {
        anchorRestProps.onClick?.(evt)
      }
    }

    return (
      <a
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        aria-describedby={ariaLabelledBy}
        aria-disabled={isDisabled}
        {...anchorRestProps}
        ref={ref as typeof anchorRestProps['ref']}
        tabIndex={linkTabIndex}
        className={buttonClasses}
        onClick={handleAnchorClick}
      >
        {content}
      </a>
    )
  } else if (isRouterLinkProps(restProps)) {
    const { as: LinkComponent, ...routerLinkRestProps } = restProps as ButtonAsRouterLinkProps<
      typeof ReactRouterLink
    >

    const handleRouterLinkClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        evt.preventDefault()
      } else {
        routerLinkRestProps.onClick?.(evt)
      }
    }
    return (
      <LinkComponent
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        aria-describedby={ariaLabelledBy}
        {...routerLinkRestProps}
        ref={ref as typeof routerLinkRestProps['ref']}
        tabIndex={linkTabIndex}
        className={buttonClasses}
        onClick={handleRouterLinkClick}
      >
        {content}
      </LinkComponent>
    )
  } else {
    const { as: _, ...btnRestProps } = restProps as ButtonAsButtonProps<'button'>
    return (
      <button
        {...cleanProps(btnRestProps, [], [])}
        aria-label={ariaLabel}
        ref={ref as React.Ref<HTMLButtonElement>}
        type={btnRestProps.type ?? 'button'}
        aria-expanded={ariaExpanded}
        aria-describedby={ariaLabelledBy}
        tabIndex={tabIndex}
        className={buttonClasses}
        disabled={isDisabled}
      >
        {content}
      </button>
    )
  }
}

export const BtnV2 = fixedForwardRef(BtnV)
